import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { StatusLabel } from '@euler/app/components/StatusLabel';
import { Typography } from '@euler/app/components/typography';
import { useCurrentStore } from '@euler/app/flows/auth/context';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { TimeElapsedLabel } from '@euler/app/flows/task/pages/detail/components/TimeElapsedLabel';
import { Avatar } from '@euler/components/Avatar';
import { StatusColors } from '@euler/components/Colors';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleInspectionTask } from '@euler/model/entity';
import {
  CommonTaskStatus,
  InspectionTaskStatus,
  SamplingCheckResultType,
  StoreFeature,
} from '@euler/model/enum';
import { parseTagsFromString } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import isBefore from 'date-fns/isBefore';
import { LinearGradient } from 'expo-linear-gradient';
import { ReactNode, memo, useCallback } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const ChildTaskListItem = memo(
  ({
    task,
    style,
    onViewDetail,
  }: {
    task: VehicleInspectionTask;
    style?: StyleProp<ViewStyle>;
    onViewDetail?: (task: VehicleInspectionTask) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const store = useCurrentStore();
    const samplingCheck = useStoreFeature(StoreFeature.SamplingCheck).enabled;
    const { storeConfiguration } = useTaskContext();

    const onViewDetailPress = useCallback(() => {
      onViewDetail?.(task);
    }, [onViewDetail, task]);

    const headerBgColors: string[] = task.isRollback
      ? ['#B381B3', '#fc7d7b']
      : task.status === InspectionTaskStatus.Pending
      ? ['#999', '#ccc']
      : task.status === InspectionTaskStatus.InProgress
      ? ['#009fd4', '#10c9bd']
      : task.status === InspectionTaskStatus.Finished
      ? [StatusColors.Success, '#6bbf6a']
      : [StatusColors.Warn, StatusColors.Notice];

    return (
      <View
        style={style}
        css={`
          background-color: #fff;
          border-radius: ${rpx(10)}px;
          overflow: hidden;
        `}
      >
        <TouchableOpacity
          onPress={onViewDetailPress}
          css={`
            min-height: ${rpx(44)}px;
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
            flex-direction: row;
            justify-content: space-between;
            flex-wrap: nowrap;
            align-items: center;
          `}
        >
          <LinearGradient
            colors={headerBgColors}
            start={[0, 0]}
            end={[1, 0]}
            style={StyleSheet.absoluteFill}
          />
          <Typography.Text medium size={rpx(18)} color="#fff">
            {parseTagsFromString(task.tags ?? '').join('/')}
          </Typography.Text>
          <LinkButton
            text="查看详情 >"
            onPress={onViewDetailPress}
            textStyle={{
              fontSize: rpx(13),
              color: 'rgba(255, 255, 255, 0.65)',
            }}
          />
        </TouchableOpacity>

        <StackLayout
          divider={
            <View
              css={`
                height: ${StyleSheet.hairlineWidth}px;
                background-color: ${Platform.OS === 'web' ? '#eee' : '#ddd'};
              `}
            />
          }
          css={`
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
            padding-bottom: ${rpx(4)}px;
          `}
        >
          <TaskStatusRow
            status={task.status}
            estimatedFinishTime={task.estimatedFinishTime}
            isRollback={task.isRollback}
            createdAt={task.createdAt}
            startedAt={task.startedAt}
            finishedAt={task.finishedAt}
          />

          <InfoRow label="服务顾问">
            <StackLayout
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              flexWrap="nowrap"
            >
              <Avatar
                name={task.serviceAgentName}
                width={rpx(18)}
                height={rpx(18)}
                borderRadius={rpx(9)}
              />
              <Typography.Label medium size={rpx(12)} color="#555" ml={rpx(4)}>
                {task.serviceAgentName}
              </Typography.Label>
            </StackLayout>
          </InfoRow>

          <InfoRow label="订单编号" value={task.orderNo} />
          <InfoRow label="工单编号" value={task.taskNo} />
          <InfoRow label="创建时间" value={formatDateTime(task.createdAt)} />
          {task.estimatedFinishTime ? (
            <InfoRow
              label="预计完工"
              value={formatDateTime(task.estimatedFinishTime)}
            />
          ) : null}
          {task.status === InspectionTaskStatus.Finished ? (
            <InfoRow
              label="完成时间"
              value={formatDateTime(task.finishedAt!)}
            />
          ) : null}
          {task.status === InspectionTaskStatus.Cancelled ? (
            <InfoRow
              label="取消时间"
              value={formatDateTime(task.canceledAt!)}
            />
          ) : null}
          {task.status === InspectionTaskStatus.Cancelled &&
          task.cancelReason ? (
            <InfoRow label="取消原因" value={task.cancelReason} />
          ) : null}

          {task.isEntryTask &&
            task.status !== InspectionTaskStatus.Cancelled && (
              <StatusRow
                label="接车预检"
                taskStatus={task.status}
                status={task.preInspectionStatus}
                startedAt={task.preInspectionStartedAt}
                finishedAt={task.preInspectedAt}
              />
            )}
          {task.status !== InspectionTaskStatus.Cancelled && (
            <StatusRow
              label="常规检测"
              taskStatus={task.status}
              status={task.inspectionStatus}
              startedAt={task.inspectionStartedAt}
              finishedAt={task.inspectedAt}
            />
          )}
          {task.status !== InspectionTaskStatus.Cancelled && (
            <StatusRow
              label="施工反馈"
              taskStatus={task.status}
              status={task.constructionStatus}
              startedAt={
                task.constructionStartedAt ?? task.constructionJobScheduledAt
              }
              finishedAt={task.constructedAt}
            />
          )}
          {task.status !== InspectionTaskStatus.Cancelled && (
            <StatusRow
              label="竣工检验"
              taskStatus={task.status}
              status={task.deliveryCheckStatus}
              startedAt={task.deliveryCheckStartedAt}
              finishedAt={task.deliveryCheckedAt}
            />
          )}
          {task.status !== InspectionTaskStatus.Cancelled &&
            (task.samplingCheckStatus !== CommonTaskStatus.Pending ||
              (store?.isIndependentDeliveryCheckEnabled && samplingCheck) ||
              (!store?.isIndependentDeliveryCheckEnabled &&
                samplingCheck &&
                storeConfiguration.allowSamplingCheckAfterFinish &&
                task.status === InspectionTaskStatus.Finished)) && (
              <StatusRow
                label="完工抽检"
                subLabel={
                  task.samplingCheckStatus === CommonTaskStatus.Finished ? (
                    <Typography.Text
                      color={
                        task.samplingCheckResult ===
                        SamplingCheckResultType.Passed
                          ? StatusColors.Success
                          : StatusColors.Danger
                      }
                      size={rpx(10)}
                      medium
                    >
                      {task.samplingCheckResult ===
                      SamplingCheckResultType.Passed
                        ? '(抽检合格)'
                        : '(抽检不合格)'}
                    </Typography.Text>
                  ) : undefined
                }
                taskStatus={task.status}
                status={task.samplingCheckStatus}
                startedAt={task.samplingCheckStartedAt}
                finishedAt={task.samplingCheckedAt}
                statusText={
                  task.status === InspectionTaskStatus.Finished &&
                  storeConfiguration.allowSamplingCheckAfterFinish &&
                  task.samplingCheckStatus === CommonTaskStatus.Pending
                    ? '可抽检'
                    : undefined
                }
              />
            )}
        </StackLayout>
      </View>
    );
  },
);

const StatusRow = memo(
  ({
    label,
    subLabel,
    taskStatus,
    status,
    statusText,
    createdAt,
    startedAt,
    finishedAt,
    style,
  }: {
    label: string;
    subLabel?: ReactNode;
    taskStatus: InspectionTaskStatus;
    status: CommonTaskStatus;
    statusText?: string;
    startedAt?: string | Date | null;
    finishedAt?: string | Date | null;
    createdAt?: string | Date | null;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={style}
        css={`
          padding-top: ${rpx(3)}px;
          padding-bottom: ${rpx(3)}px;
        `}
      >
        <StackLayout direction="row" alignItems="center" gap={rpx(5)}>
          <Typography.Label light size={rpx(14)} color="#333">
            {label}
          </Typography.Label>
          {subLabel}
        </StackLayout>
        <StackLayout
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={rpx(10)}
        >
          <TimeElapsedLabel
            textStyle={{ lineHeight: rpx(20) }}
            style={{ marginTop: 0, opacity: 0.75 }}
            flow={{
              status:
                status === CommonTaskStatus.Finished
                  ? 'finished'
                  : status === CommonTaskStatus.InProgress
                  ? 'inprogress'
                  : 'pending',
              finishedAt,
              startedAt,
              createdAt,
            }}
          />
          <StatusLabel
            status={status}
            text={
              statusText ??
              (taskStatus === InspectionTaskStatus.Finished &&
              status === CommonTaskStatus.Pending
                ? '未进行'
                : undefined)
            }
            textStyle={[
              { fontSize: rpx(11), fontWeight: '500' },
              status === CommonTaskStatus.Pending
                ? { color: '#777' }
                : undefined,
            ]}
            style={[
              {
                height: rpx(18),
                paddingHorizontal: Platform.OS === 'web' ? rpx(8) : rpx(2),
              },
              status === CommonTaskStatus.Pending
                ? {
                    backgroundColor: '#ddd',
                  }
                : undefined,
            ]}
          />
        </StackLayout>
      </StackLayout>
    );
  },
);

const TaskStatusRow = memo(
  ({
    status,
    style,
    isRollback,
    estimatedFinishTime,
    ...props
  }: {
    status: InspectionTaskStatus;
    isRollback: boolean;
    startedAt?: string | Date | null;
    finishedAt?: string | Date | null;
    createdAt?: string | Date | null;
    style?: StyleProp<ViewStyle>;
    estimatedFinishTime?: string | Date | null;
  }) => {
    const { rpx } = useResponsiveContext();
    const hasTimedOut =
      estimatedFinishTime != null &&
      isBefore(dateFromValue(estimatedFinishTime), new Date());
    const color = isRollback
      ? '#ed1e78'
      : status === InspectionTaskStatus.Pending
      ? StatusColors.Warn
      : status === InspectionTaskStatus.InProgress
      ? StatusColors.Info
      : status === InspectionTaskStatus.Finished
      ? StatusColors.Success
      : StatusColors.Danger;
    const text = isRollback
      ? '返单中'
      : status === InspectionTaskStatus.Pending
      ? '未开始'
      : status === InspectionTaskStatus.InProgress
      ? '服务中'
      : status === InspectionTaskStatus.Finished
      ? '已完成'
      : '已取消';
    return (
      <InfoRow label="服务状态" style={style} {...props}>
        {status !== InspectionTaskStatus.Cancelled && (
          <TimeElapsedLabel
            textStyle={{ lineHeight: rpx(20) }}
            style={{ marginTop: 0, opacity: 0.75 }}
            flow={{
              status:
                status === InspectionTaskStatus.Finished
                  ? 'finished'
                  : status === InspectionTaskStatus.InProgress
                  ? 'inprogress'
                  : 'pending',
              finishedAt: props.finishedAt,
              startedAt: props.startedAt,
              createdAt: props.createdAt,
            }}
          />
        )}
        <Typography.Text medium size={rpx(14)} color={color}>
          {text}
        </Typography.Text>
        {hasTimedOut ? (
          <Typography.Text medium size={rpx(14)} color={StatusColors.Danger}>
            (已超时)
          </Typography.Text>
        ) : null}
      </InfoRow>
    );
  },
);

const InfoRow = memo(
  ({
    label,
    value,
    children,
    style,
  }: {
    label: string;
    value?: string;
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={style}
        css={`
          padding-top: ${rpx(3)}px;
          padding-bottom: ${rpx(3)}px;
        `}
      >
        <Typography.Label light size={rpx(14)} color="#333">
          {label}
        </Typography.Label>
        <StackLayout
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={rpx(5)}
        >
          {value ? (
            <Typography.Text medium size={rpx(14)} color="#777">
              {value}
            </Typography.Text>
          ) : null}
          {children}
        </StackLayout>
      </StackLayout>
    );
  },
);
